import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { buildAccountDetailsModel } from '../model/account-details.model.js';
import { isPendingVerificationAccount } from '../model/account-platform-state.js';
import { buildSyncSuccessMessage } from '../dto/accounts-list.dto.js';
import { buildAccountDetailsTabs, ACCOUNT_DETAILS_TABS } from '../model/account-details-tabs.model.js';
import {
  ADMIN_REALTIME_EVENT,
  ADMIN_REALTIME_TYPES,
} from '../../../shared/realtime/admin-realtime-events.js';

export function mergeRealtimeAccount(account, event, routeAccountId = '') {
  if (!account) {
    return account;
  }

  const eventAccountId = String(event?.accountId || event?.uid || '').trim();
  const currentAccountIds = [
    account?.uid,
    account?.id,
    routeAccountId,
  ].map((value) => String(value || '').trim()).filter(Boolean);
  if (!eventAccountId || !currentAccountIds.includes(eventAccountId)) {
    return account;
  }

  const sync = event?.tpAccountSyncDTO || event?.tpAccountSync || event?.sync || null;

  return {
    ...account,
    ...event,
    uid: account.uid || event.uid || event.accountId,
    id: account.id || event.id,
    type: event.accountType || event.typeLabel || account.type,
    profile: account.profile,
    tpAccountSyncDTO: sync ? {
      ...(account.tpAccountSyncDTO || account.tpAccountSync || account.sync || {}),
      ...sync,
    } : account.tpAccountSyncDTO,
  };
}

export function useAccountDetails() {
  const { i18n } = useTranslation();
  const locale = i18n?.resolvedLanguage || i18n?.language;
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = location.state?.from || '/accounts';
  const backLabel = location.state?.from && location.state.from.startsWith('/profiles/')
    ? 'Back to Profile'
    : 'Back to Accounts';
  const [activeTab, setActiveTab] = useState(ACCOUNT_DETAILS_TABS.details);
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncingId, setSyncingId] = useState('');
  const [syncError, setSyncError] = useState('');
  const [syncSuccess, setSyncSuccess] = useState('');
  const latestRealtimeAccountRef = useRef(null);

  // Trades state
  const [trades, setTrades] = useState([]);
  const [tradesTotal, setTradesTotal] = useState(0);
  const [tradesTotalLots, setTradesTotalLots] = useState('0');
  const [tradesTotalProfit, setTradesTotalProfit] = useState('0');
  const [tradesProfitCurrency, setTradesProfitCurrency] = useState('USD');
  const [isTradesLoading, setIsTradesLoading] = useState(false);
  const [tradesError, setTradesError] = useState('');
  const [tradesOffset, setTradesOffset] = useState(0);
  const [tradesSymbolFilter, setTradesSymbolFilter] = useState('');
  const [tradesDateFromFilter, setTradesDateFromFilter] = useState('');
  const [tradesDateToFilter, setTradesDateToFilter] = useState('');

  const tabs = useMemo(() => buildAccountDetailsTabs(account), [account]);

  // Load account detail
  useEffect(() => {
    let isCancelled = false;
    if (!id) {
      setAccount(null);
      setError('Account id is required');
      setIsLoading(false);
      return () => { isCancelled = true; };
    }

    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await adminApi.getAccountDetail(token, id);
        if (!isCancelled) {
          const loadedAccount = response?.account || response;
          setAccount(mergeRealtimeAccount(loadedAccount, latestRealtimeAccountRef.current, id));
        }
      } catch (requestError) {
        if (!isCancelled) {
          setAccount(null);
          setError(requestError.message || 'Failed to load account details');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    load();
    return () => { isCancelled = true; };
  }, [id, token]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const listener = (event) => {
      const realtimeEvent = event?.detail || {};
      if (realtimeEvent?.type !== ADMIN_REALTIME_TYPES.accountSynced) {
        return;
      }

      latestRealtimeAccountRef.current = realtimeEvent;
      setAccount((currentAccount) => mergeRealtimeAccount(currentAccount || { uid: id }, realtimeEvent, id));
    };

    window.addEventListener(ADMIN_REALTIME_EVENT, listener);

    return () => {
      window.removeEventListener(ADMIN_REALTIME_EVENT, listener);
    };
  }, [id]);

  // Load trades (lazy on tab)
  useEffect(() => {
    let isCancelled = false;
    if (activeTab !== ACCOUNT_DETAILS_TABS.trades || !id || !account || account.capabilities?.supportsClosedTrades === false) {
      return undefined;
    }

    const loadTrades = async () => {
      setIsTradesLoading(true);
      setTradesError('');
      try {
        const filters = { limit: 50, offset: tradesOffset };
        if (tradesSymbolFilter) filters.symbol = tradesSymbolFilter;
        if (tradesDateFromFilter) filters.dateFrom = tradesDateFromFilter;
        if (tradesDateToFilter) filters.dateTo = tradesDateToFilter;

        const response = await adminApi.getAccountTrades(token, id, filters);
        if (!isCancelled) {
          const newTrades = Array.isArray(response?.trades) ? response.trades : [];
          setTrades((prev) => tradesOffset === 0 ? newTrades : [...prev, ...newTrades]);
          setTradesTotal(response?.total ?? 0);
          setTradesTotalLots(response?.totalLots ?? '0');
          setTradesTotalProfit(response?.totalProfit ?? '0');
          setTradesProfitCurrency(response?.profitCurrency ?? 'USD');
        }
      } catch (requestError) {
        if (!isCancelled) {
          if (tradesOffset === 0) setTrades([]);
          setTradesError(requestError.message || 'Failed to load trades');
        }
      } finally {
        if (!isCancelled) {
          setIsTradesLoading(false);
        }
      }
    };

    loadTrades();
    return () => { isCancelled = true; };
  }, [activeTab, account, id, token, tradesOffset, tradesSymbolFilter, tradesDateFromFilter, tradesDateToFilter]);

  const syncAccount = useCallback(async () => {
    if (!id) return;
    if (account && isPendingVerificationAccount(account)) return;

    setSyncingId(id);
    setSyncError('');
    setSyncSuccess('');
    try {
      const response = await adminApi.syncAccount(token, id);
      setSyncSuccess(buildSyncSuccessMessage(response));
    } catch (requestError) {
      setSyncError(requestError.message || 'Failed to sync account');
    } finally {
      setSyncingId('');
    }
  }, [account, id, token]);

  const handleTradesSymbolChange = useCallback((value) => {
    setTradesOffset(0);
    setTrades([]);
    setTradesSymbolFilter(value);
  }, []);

  const handleTradesDateFromChange = useCallback((value) => {
    setTradesOffset(0);
    setTrades([]);
    setTradesDateFromFilter(value);
  }, []);

  const handleTradesDateToChange = useCallback((value) => {
    setTradesOffset(0);
    setTrades([]);
    setTradesDateToFilter(value);
  }, []);

  const loadMoreTrades = useCallback(() => {
    setTradesOffset((prev) => prev + trades.length);
  }, [trades.length]);

  const goBack = useCallback(() => {
    navigate(backTo);
  }, [navigate, backTo]);

  const details = account ? buildAccountDetailsModel(account, locale) : null;

  return {
    tabs,
    activeTab,
    setActiveTab,
    account,
    details,
    isLoading,
    error,
    syncingId,
    syncError,
    syncSuccess,
    syncAccount,
    goBack,
    backLabel,
    trades,
    tradesTotal,
    tradesTotalLots,
    tradesTotalProfit,
    tradesProfitCurrency,
    isTradesLoading,
    tradesError,
    tradesSymbolFilter,
    tradesDateFromFilter,
    tradesDateToFilter,
    handleTradesSymbolChange,
    handleTradesDateFromChange,
    handleTradesDateToChange,
    loadMoreTrades,
  };
}
